import { updateLinkedinAccountLimits, updateLinkedinAccountWorkTime } from 'API/linkedin-account-limits';
import { getLinkedinAccountById } from 'API/linkedin-accounts';
import { FunnelBasic, getFunnelsOfLinkedinAccount, LimitWeights, updateLinkedinFunnelLimitWeightsAlt } from 'API/linkedin-funnel-limit-weights';
import { FunnelDays } from 'features/co-pilot/co-pilot.types';
import { LinkedinAccount } from 'features/co-pilot/linkedin-accounts/linkedin-accounts.types';
import { OptionType } from 'features/ui/base-components/pai-auto-complete';
import _ from 'lodash';
import { Dispatch, SetStateAction, useEffect, useMemo, useRef, useState } from 'react';

export interface AccountLimitsState {
  connectionsLimit: number | null;
  messagesLimit: number | null;
  emailsLimit: number | null;
  coldCallsLimit: number | null;
  cooldownAfterMessages: number | null;
  cooldownAfterVisits: number | null;
  visitDailyLimit: number | null;
  inmailDailyLimit: number | null;
}

export interface WorktimeState {
  workDays: FunnelDays[];
  workHours: number[];
  timeZone: OptionType;
}

interface UseLinkedinAccountLimitsReturn {
  fullLinkedinAccount: LinkedinAccount | undefined;
  setFullLinkedinAccount: Dispatch<SetStateAction<LinkedinAccount | undefined>>;
  linkedinAccountLoading: boolean;
  linkedinAccountUpdating: boolean;

  funnels: FunnelBasic[];
  editedWeights: Record<string, LimitWeights>;

  setWorkDays: (value: FunnelDays[]) => void;

  setWorkHours: (value: number[]) => void;

  setTimeZone: (tz: OptionType) => void;

  updateLimit: (field: keyof AccountLimitsState, value: number | null) => void;

  handleTimeZoneChange: (event: React.ChangeEvent<HTMLInputElement>, newValue: OptionType) => void;

  handleWeightChange: (funnelId: string, metric: keyof LimitWeights, value: number) => void;
}

/**
 * Hook that loads & updates work-time + limit data for a LinkedIn account.
 * All PATCH calls are debounced (1 s) to avoid spamming the backend while the user types.
 * Each limit field has its own debounced function to prevent race conditions when updating multiple fields quickly.
 */
export default function useLinkedinAccountLimits(
  accountId: string | undefined,
  funnelId: string | undefined
): UseLinkedinAccountLimitsReturn {
  const [fullLinkedinAccount, setFullLinkedinAccount] = useState<LinkedinAccount | undefined>();
  const [linkedinAccountLoading, setLinkedinAccountLoading] = useState(false);
  const [linkedinAccountUpdating, setLinkedinAccountUpdating] = useState(false);

  const [funnels, setFunnels] = useState<FunnelBasic[]>([]);
  const [editedWeights, setEditedWeights] = useState<Record<string, LimitWeights>>({});

  const ongoingUpdatesRef = useRef(0);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  /**
   * Get full linkedin account
   */

  useEffect(() => {
    if (!_.isNil(accountId) && !_.isNil(funnelId)) {
      (async () => {
        setLinkedinAccountLoading(true);
        const res = await getLinkedinAccountById(accountId);

        if (!res.error) {
          const acc = res as any;
          const dailyLimits = acc.dailyLimits ?? {};
          const mappedWorkTime = {
            workDays: acc.workDays ?? [],
            workHours: acc.workHours ?? [],
            // Backend stores this as `timezone`; settings UI expects `timeZone`.
            timeZone: acc.timeZone ?? acc.timezone ?? 'Europe/Berlin',
          };
          const mappedLimits = {
            connectionsLimit: acc.connectionsLimit ?? acc.connectionDailyLimit ?? dailyLimits.connections ?? null,
            messagesLimit: acc.messagesLimit ?? acc.messageDailyLimit ?? dailyLimits.messages ?? null,
            emailsLimit: acc.emailsLimit ?? acc.emailDailyLimit ?? null,
            coldCallsLimit: acc.coldCallsLimit ?? acc.coldCallDailyLimit ?? null,
            cooldownAfterMessages: acc.cooldownAfterMessages ?? null,
            cooldownAfterVisits: acc.cooldownAfterVisits ?? null,
            visitDailyLimit: acc.visitDailyLimit ?? dailyLimits.visits ?? null,
            inmailDailyLimit: acc.inmailDailyLimit ?? dailyLimits.inmails ?? null,
          };
          setFullLinkedinAccount({ ...acc, ...mappedWorkTime, ...mappedLimits });
        }
        setLinkedinAccountLoading(false);
      })();
    } else {
      setFullLinkedinAccount(undefined);
    }
  }, [accountId, funnelId]);

  useEffect(() => {
    const fetchFunnels = async () => {
      if (accountId) {
        const res = await getFunnelsOfLinkedinAccount(accountId, funnelId);
        if (!res.error) {
          const fetchedFunnels = (res.data as FunnelBasic[]) || [];
          setFunnels(fetchedFunnels);
          setEditedWeights(fetchedFunnels.reduce((acc, f) => ({ ...acc, [f._id]: { ...f.limitWeights } }), {}));
        }
      } else {
        setFunnels([]);
        setEditedWeights({});
      }
    };
    fetchFunnels();
  }, [accountId, funnelId]);

  const handleLinkedinAccountWorktimeUpdate = async (payload: any) => {
    if (accountId) {
      setLinkedinAccountUpdating(true);
      ongoingUpdatesRef.current++;
      const res = await updateLinkedinAccountWorkTime(accountId, payload);
      if (!res.error) {
        timeoutRef.current = await new Promise((resolve) =>
          setTimeout(() => {
            ongoingUpdatesRef.current -= 1;

            if (ongoingUpdatesRef.current === 0) {
              setLinkedinAccountUpdating(false);
            }

            resolve(timeoutRef.current);
          }, 1.5 * 1000)
        );
      } else {
        if (!_.isNil(timeoutRef.current)) {
          clearTimeout(timeoutRef.current);
        }
        if (!_.isNil(ongoingUpdatesRef.current)) {
          ongoingUpdatesRef.current = 0;
        }

        setLinkedinAccountUpdating(false);
      }
    }
  };

  const handleLinkedinAccountLimitsUpdate = async (payload: any) => {
    if (accountId) {
      setLinkedinAccountUpdating(true);
      ongoingUpdatesRef.current++;
      const res = await updateLinkedinAccountLimits(accountId, payload);
      if (!res.error) {
        timeoutRef.current = await new Promise((resolve) =>
          setTimeout(() => {
            ongoingUpdatesRef.current -= 1;

            if (ongoingUpdatesRef.current === 0) {
              setLinkedinAccountUpdating(false);
            }

            resolve(timeoutRef.current);
          }, 1.5 * 1000)
        );
      } else {
        if (!_.isNil(timeoutRef.current)) {
          clearTimeout(timeoutRef.current);
        }
        if (!_.isNil(ongoingUpdatesRef.current)) {
          ongoingUpdatesRef.current = 0;
        }
      }
    }
  };

  // Debounced worktime patch functions - separate for each field to prevent race conditions
  const debouncedWorktimePatchers = useMemo(
    () => ({
      workDays: _.debounce(async (payload: any) => {
        await handleLinkedinAccountWorktimeUpdate(payload);
      }, 1000),
      workHours: _.debounce(async (payload: any) => {
        await handleLinkedinAccountWorktimeUpdate(payload);
      }, 1000),
      timeZone: _.debounce(async (payload: any) => {
        await handleLinkedinAccountWorktimeUpdate(payload);
      }, 1000),
    }),
    [accountId]
  );

  // Debounced limit patchers
  const debouncedLimitsPatchers = useMemo(
    () => ({
      connectionsLimit: _.debounce(async (payload: any) => {
        await handleLinkedinAccountLimitsUpdate({
          connectionDailyLimit: payload.connectionsLimit,
          connectionsLimit: payload.connectionsLimit,
        });
      }, 1000),
      messagesLimit: _.debounce(async (payload: any) => {
        await handleLinkedinAccountLimitsUpdate({
          messageDailyLimit: payload.messagesLimit,
          messagesLimit: payload.messagesLimit,
        });
      }, 1000),
      emailsLimit: _.debounce(async (payload: any) => {
        await handleLinkedinAccountLimitsUpdate({
          emailDailyLimit: payload.emailsLimit,
          emailsLimit: payload.emailsLimit,
        });
      }, 1000),
      coldCallsLimit: _.debounce(async (payload: any) => {
        await handleLinkedinAccountLimitsUpdate({
          coldCallDailyLimit: payload.coldCallsLimit,
          coldCallsLimit: payload.coldCallsLimit,
        });
      }, 1000),
      cooldownAfterMessages: _.debounce(async (payload: any) => {
        await handleLinkedinAccountLimitsUpdate(payload);
      }, 1000),
      cooldownAfterVisits: _.debounce(async (payload: any) => {
        await handleLinkedinAccountLimitsUpdate(payload);
      }, 1000),
      visitDailyLimit: _.debounce(async (payload: any) => {
        await handleLinkedinAccountLimitsUpdate({
          visitDailyLimit: payload.visitDailyLimit,
        });
      }, 1000),
      inmailDailyLimit: _.debounce(async (payload: any) => {
        await handleLinkedinAccountLimitsUpdate({
          inmailDailyLimit: payload.inmailDailyLimit,
        });
      }, 1000),
    }),
    [accountId]
  );

  const handleWeightUpdate = async (funnelId: string, weights: LimitWeights) => {
    if (funnelId) {
      setLinkedinAccountUpdating(true);
      ongoingUpdatesRef.current++;
      const res = await updateLinkedinFunnelLimitWeightsAlt(funnelId, { limitWeights: weights });
      if (!res.error) {
        timeoutRef.current = await new Promise((resolve) =>
          setTimeout(() => {
            ongoingUpdatesRef.current -= 1;

            if (ongoingUpdatesRef.current === 0) {
              setLinkedinAccountUpdating(false);
            }

            resolve(timeoutRef.current);
          }, 1.5 * 1000)
        );
      } else {
        if (!_.isNil(timeoutRef.current)) {
          clearTimeout(timeoutRef.current);
        }
        if (!_.isNil(ongoingUpdatesRef.current)) {
          ongoingUpdatesRef.current = 0;
        }
        setLinkedinAccountUpdating(false);
      }
    }
  };

  // Debounced weight updates per funnel to avoid cross-funnel race conditions
  const debouncedWeightUpdateRef = useRef<Record<string, _.DebouncedFunc<any>>>({});

  const getDebouncedWeightUpdater = (funnelId: string) => {
    if (!debouncedWeightUpdateRef.current[funnelId]) {
      debouncedWeightUpdateRef.current[funnelId] = _.debounce(async (weights: LimitWeights) => {
        await handleWeightUpdate(funnelId, weights);
      }, 1000);
    }
    return debouncedWeightUpdateRef.current[funnelId];
  };

  // Cleanup debounced functions on unmount
  useEffect(() => {
    return () => {
      Object.values(debouncedWorktimePatchers).forEach((debouncedFunc: _.DebouncedFunc<any>) => {
        debouncedFunc.cancel();
      });
      Object.values(debouncedLimitsPatchers).forEach((debouncedFunc: _.DebouncedFunc<any>) => {
        debouncedFunc.cancel();
      });
      Object.values(debouncedWeightUpdateRef.current).forEach((debouncedFunc: _.DebouncedFunc<any>) => {
        debouncedFunc.cancel();
      });
    };
  }, [debouncedWorktimePatchers, debouncedLimitsPatchers]);

  /**
   * Setters
   */

  const setWorkDays = (value: FunnelDays[]) => {
    setFullLinkedinAccount((prev) => ({ ...prev!, workDays: value }));
    debouncedWorktimePatchers.workDays({ workDays: value });
  };

  const setWorkHours = (value: number[]) => {
    setFullLinkedinAccount((prev) => ({ ...prev!, workHours: value }));
    debouncedWorktimePatchers.workHours({ workHours: value.sort((a, b) => a - b) });
  };

  const setTimeZone = (tz: OptionType) => {
    setFullLinkedinAccount((prev) => ({ ...prev!, timeZone: tz.value }));
    debouncedWorktimePatchers.timeZone({ timeZone: tz.value });
  };

  const updateLimit = (field: keyof AccountLimitsState, value: number | null) => {
    setFullLinkedinAccount((prev) => ({ ...prev!, [field]: value }));
    // Use the individual debounced function for this specific field
    debouncedLimitsPatchers[field]({ [field]: value });
  };

  const handleTimeZoneChange = (event: React.ChangeEvent<HTMLInputElement>, newValue: OptionType) => {
    setTimeZone(newValue);
  };

  const handleWeightChange = (funnelId: string, metric: keyof LimitWeights, value: number) => {
    setEditedWeights((prev) => {
      const updatedWeights = {
        ...prev,
        [funnelId]: {
          ...prev[funnelId],
          [metric]: value,
        },
      };

      // Trigger debounced auto-save for this specific funnel
      const funnelWeights = updatedWeights[funnelId];
      if (funnelWeights) {
        getDebouncedWeightUpdater(funnelId)(funnelWeights);
      }

      return updatedWeights;
    });
  };

  return {
    fullLinkedinAccount,
    setFullLinkedinAccount,
    linkedinAccountLoading,
    linkedinAccountUpdating,
    funnels,
    editedWeights,
    setWorkDays,
    setWorkHours,
    setTimeZone,
    updateLimit,
    handleTimeZoneChange,
    handleWeightChange,
  };
}
